'use client';

import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db';
import { acceptTerms, hasAcceptedTerms } from '@/app/serverActions/termsAcceptance';
import React, { useState, useEffect, useRef } from 'react';

export const useTermsAcceptance = (userId: string | null | undefined, auctionId: number) => {
    const [isAccepted, setIsAccepted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hasCheckedServer, setHasCheckedServer] = useState(false);
    const queryStartedRef = useRef(false);
    const queryCompletedRef = useRef(false);

    // Query local database
    const localAcceptance = useLiveQuery(
        async () => {
            if (!userId) return undefined;
            queryStartedRef.current = true;
            const result = await db.termsAcceptances.where({ userId, auctionId }).first();
            queryCompletedRef.current = true;
            return result || null;
        },
        [userId, auctionId],
        undefined
    );

    useEffect(() => {
        // Reset state when userId or auctionId changes
        setHasCheckedServer(false);
        setIsLoading(true);
        setIsAccepted(false);
        queryStartedRef.current = false;
        queryCompletedRef.current = false;
    }, [userId, auctionId]);

    useEffect(() => {
        if (!userId) {
            setIsLoading(false);
            setIsAccepted(false);
            return;
        }

        // Wait until we know the query has been initiated and completed
        if (!queryStartedRef.current || !queryCompletedRef.current) {
            return;
        }

        // If we found a local record, use it
        if (localAcceptance !== null && localAcceptance !== undefined) {
            setIsAccepted(localAcceptance.accepted);
            setIsLoading(false);
            return;
        }

        // If no local record and haven't checked server yet, sync from server
        if (!hasCheckedServer) {
            setHasCheckedServer(true);
            
            hasAcceptedTerms(userId, auctionId)
                .then(async (serverState) => {
                    try {
                        // Check if record already exists
                        const existing = await db.termsAcceptances
                            .where({ userId, auctionId })
                            .first();
                        
                        if (existing) {
                            // Update existing record
                            await db.termsAcceptances.update(existing.id!, { accepted: serverState });
                        } else {
                            // Create new record
                            await db.termsAcceptances.add({ userId, auctionId, accepted: serverState });
                        }
                        setIsAccepted(serverState);
                    } catch (error) {
                        console.error("Failed to save terms acceptance to local DB:", error);
                        setIsAccepted(serverState);
                    }
                    setIsLoading(false);
                })
                .catch((e) => {
                    console.error("Failed to sync terms acceptance from server", e);
                    // On error, assume not accepted
                    setIsAccepted(false);
                    setIsLoading(false);
                });
        } else {
            // We've checked everything and found nothing
            setIsAccepted(false);
            setIsLoading(false);
        }
    }, [localAcceptance, userId, auctionId, hasCheckedServer]);

    const accept = async () => {
        if (!userId || isAccepted) {
            return;
        }
        try {
            await acceptTerms(userId, auctionId);
            
            // Check if record already exists
            const existing = await db.termsAcceptances
                .where({ userId, auctionId })
                .first();
            
            if (existing) {
                // Update existing record
                await db.termsAcceptances.update(existing.id!, { accepted: true });
            } else {
                // Create new record
                await db.termsAcceptances.add({ userId, auctionId, accepted: true });
            }
            
            setIsAccepted(true);
        } catch (error) {
            console.error("Failed to accept terms:", error);
        }
    };

    return { isAccepted, isLoading, accept };
}; 